import { type KeyboardEvent, type MouseEvent, type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import Tooltip from '@/components/atoms/Tooltip';
import { useRovingTabs } from '@/hooks/useRovingTabs';

import styles from './SegmentedControl.module.scss';

export interface SegmentedControlOption<V extends string> {
    value: V;
    label: ReactNode;
    disabled?: boolean;
    tooltip?: ReactNode;
    /** Makes the segment a router link; the caller then derives `value` from the location. */
    to?: string;
}

export interface SegmentedControlProps<V extends string> {
    options: ReadonlyArray<SegmentedControlOption<V>>;
    /** Controlled selection. */
    value?: V;
    /** Uncontrolled starting selection, for callers that only listen. */
    defaultValue?: V;
    /** Called with the segment's own click event, also when the arrow keys select it. */
    onChange?: (value: V, event: MouseEvent<HTMLElement>) => void;
    'aria-label'?: string;
    size?: 'small' | 'medium';
    /** Stretches the control and shares the width equally between its segments. */
    fullWidth?: boolean;
    /** Stacks the segments, for a narrow column. */
    orientation?: 'horizontal' | 'vertical';
    className?: string;
}

/**
 * One choice out of a few, shown side by side: a mode, a view or a period. One Tab stop
 * covers the group and the arrow keys move between segments.
 *
 * Button segments pick a value in place and nothing points at a panel they control, so the
 * group is a radiogroup and each segment says whether it is checked. Link segments move to
 * another page, which is what a tab bar does, so a group with links stays a tablist.
 */
export function SegmentedControl<V extends string>(props: SegmentedControlProps<V>) {
    const {
        options,
        value,
        defaultValue,
        onChange,
        size = 'medium',
        fullWidth = false,
        orientation = 'horizontal',
        className,
    } = props;

    const [uncontrolledValue, setUncontrolledValue] = useState<V | undefined>(defaultValue);
    const selected = value !== undefined ? value : uncontrolledValue;
    const selectedIndex = options.findIndex((option) => option.value === selected);
    const isTabBar = options.some((option) => option.to !== undefined);

    const roving = useRovingTabs({
        count: options.length,
        selectedIndex,
        isDisabled: (index) => options[index]?.disabled === true,
        activatesOnFocus: (index) => options[index]?.to === undefined,
    });

    const select = (option: SegmentedControlOption<V>, event: MouseEvent<HTMLElement>) => {
        if (option.disabled) {
            event.preventDefault();
            return;
        }
        if (value === undefined) {
            setUncontrolledValue(option.value);
        }
        onChange?.(option.value, event);
    };

    return (
        <div
            role={isTabBar ? 'tablist' : 'radiogroup'}
            aria-label={props['aria-label']}
            aria-orientation={orientation}
            className={classNames(
                styles.segmented,
                { [styles.fullWidth]: fullWidth, [styles.vertical]: orientation === 'vertical' },
                className
            )}
            data-size={size}>
            {options.map((option, index) => {
                const isSelected = option.value === selected;
                const segmentClassName = classNames(styles.segment, {
                    [styles.selected]: isSelected,
                });
                const handleKeyDown = (event: KeyboardEvent<HTMLElement>) =>
                    roving.onKeyDown(event, index);

                const segment =
                    option.to !== undefined ? (
                        <Link
                            key={option.value}
                            ref={roving.setRef(index)}
                            role="tab"
                            aria-selected={isSelected}
                            aria-disabled={option.disabled || undefined}
                            tabIndex={roving.tabIndexFor(index)}
                            className={segmentClassName}
                            to={option.to}
                            onKeyDown={handleKeyDown}
                            onClick={(event) => select(option, event)}>
                            {option.label}
                        </Link>
                    ) : (
                        <button
                            key={option.value}
                            ref={roving.setRef(index)}
                            type="button"
                            role={isTabBar ? 'tab' : 'radio'}
                            aria-selected={isTabBar ? isSelected : undefined}
                            aria-checked={isTabBar ? undefined : isSelected}
                            tabIndex={roving.tabIndexFor(index)}
                            className={segmentClassName}
                            value={option.value}
                            disabled={option.disabled}
                            onKeyDown={handleKeyDown}
                            onClick={(event) => select(option, event)}>
                            {option.label}
                        </button>
                    );

                return option.tooltip ? (
                    <Tooltip
                        key={option.value}
                        content={option.tooltip}
                        placement="bottom"
                        layout="inline"
                        targetClassName={styles.tooltipTarget}>
                        {segment}
                    </Tooltip>
                ) : (
                    segment
                );
            })}
        </div>
    );
}

export default SegmentedControl;
